import { useEffect, useRef, useState, useCallback } from "react";
import { getLogTail } from "../lib/api";
import { CircleNotch, Pause, Play, ArrowClockwise, MagnifyingGlass, ArrowLineUp } from "@phosphor-icons/react";

const SOURCES = [
  { key: "app_errors", label: "App errors (any host)" },
  { key: "backend_err", label: "Backend (app + errors)" },
  { key: "backend_out", label: "Backend (stdout)" },
];

const LINE_CLS = (l) => {
  const s = l.toLowerCase();
  if (s.includes("traceback") || / error|error:|exception|killed|oom|reaped|memory/.test(s)) return "text-rose-400";
  if (s.includes("warning")) return "text-amber-300";
  if (/subprocess|processpoolexecutor|batch_size|pages\/batch|flush/.test(s)) return "text-violet-300";
  if (/mcc|karnataka|allotment|ingest|raw_allotments|finalize/.test(s)) return "text-emerald-300";
  return "text-zinc-300";
};

export default function AdminLogs() {
  const [source, setSource] = useState("app_errors");
  const [lines, setLines] = useState([]);
  const [count, setCount] = useState(600);
  const [live, setLive] = useState(true);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({});
  const boxRef = useRef(null);
  const atTopRef = useRef(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getLogTail({ source, lines: count });
      setLines(data.lines || []);
      setMeta({ file: data.file, size: data.size, missing: data.missing });
    } catch (e) {
      setLines([`⚠️ Failed to load logs: ${e?.response?.data?.detail || e.message}`]);
    }
    setLoading(false);
  }, [source, count]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!live) return;
    const id = setInterval(load, 2500);
    return () => clearInterval(id);
  }, [live, load]);

  useEffect(() => {
    const el = boxRef.current;
    if (el && atTopRef.current) el.scrollTop = 0;
  }, [lines]);

  const onScroll = () => {
    const el = boxRef.current;
    if (!el) return;
    atTopRef.current = el.scrollTop < 60;
  };

  const filtered = filter
    ? lines.filter((l) => l.toLowerCase().includes(filter.toLowerCase()))
    : lines;
  const shown = [...filtered].reverse(); // newest first

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-200 flex flex-col" data-testid="admin-logs-page">
      <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-3 flex-wrap sticky top-0 bg-[#09090B] z-10">
        <span className="text-sm font-black uppercase tracking-[0.2em] text-[#8B7CF6]">Live Log Relay</span>
        <div className="flex gap-1">
          {SOURCES.map((s) => (
            <button key={s.key} onClick={() => setSource(s.key)} data-testid={`logs-source-${s.key}`}
              className={`h-7 px-2.5 text-[11px] font-bold rounded ${source === s.key ? "bg-[#6043EF] text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"}`}>
              {s.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <MagnifyingGlass size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="filter…" data-testid="logs-filter"
            className="h-7 w-44 bg-zinc-900 border border-zinc-700 rounded pl-7 pr-2 text-[12px] font-mono text-zinc-200 focus:outline-none focus:border-[#6043EF]" />
        </div>
        <select value={count} onChange={(e) => setCount(Number(e.target.value))} data-testid="logs-line-count"
          className="h-7 bg-zinc-900 border border-zinc-700 rounded px-2 text-[11px] text-zinc-300">
          {[200, 600, 1200, 3000].map((v) => <option key={v} value={v}>{v} lines</option>)}
        </select>
        <button onClick={() => setLive((v) => !v)} data-testid="logs-live-toggle"
          className={`h-7 px-2.5 text-[11px] font-bold rounded inline-flex items-center gap-1 ${live ? "bg-emerald-600 text-white" : "bg-zinc-800 text-zinc-400 hover:bg-zinc-700"}`}>
          {live ? <><Pause size={12} weight="fill" /> Live</> : <><Play size={12} weight="fill" /> Paused</>}
        </button>
        <button onClick={load} data-testid="logs-refresh"
          className="h-7 px-2.5 text-[11px] font-bold rounded bg-zinc-800 text-zinc-300 hover:bg-zinc-700 inline-flex items-center gap-1">
          <ArrowClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        <button onClick={() => { atTopRef.current = true; const el = boxRef.current; if (el) el.scrollTop = 0; }}
          data-testid="logs-jump-top" className="h-7 px-2.5 text-[11px] font-bold rounded bg-zinc-800 text-zinc-300 hover:bg-zinc-700 inline-flex items-center gap-1">
          <ArrowLineUp size={12} weight="bold" /> Newest
        </button>
        <span className="text-[10px] text-zinc-500 ml-auto font-mono">
          {meta.file || "…"} · {meta.size != null ? `${(meta.size / 1048576).toFixed(1)}MB` : ""} · {shown.length}/{lines.length} lines · IST · newest first
          {live && <CircleNotch size={11} weight="bold" className="inline animate-spin ml-1.5 text-emerald-400" />}
        </span>
      </div>
      <div ref={boxRef} onScroll={onScroll} data-testid="logs-output"
        className="flex-1 overflow-auto px-4 py-3 font-mono text-[11.5px] leading-[1.5] whitespace-pre-wrap break-words">
        {shown.length === 0 ? (
          <div className="text-zinc-600">{meta.missing ? "Log file not found on this pod." : "No lines."}</div>
        ) : shown.map((l, i) => (
          <div key={i} className={LINE_CLS(l)}>{l || "\u00A0"}</div>
        ))}
      </div>
    </div>
  );
}
